import * as React from 'react';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import ToggleButtonGroup from '@mui/joy/ToggleButtonGroup';
import Typography from '@mui/joy/Typography';
import { LineChart } from '@mui/x-charts/LineChart';
import { TimeGranularity } from '../../../../models/paymentSummary';
import { BotRow, TimeRow, TrendMetric, trendDataKey } from './aggregate';
import { formatBucketLabel } from './formatBucket';

type Props = {
    timeRows: TimeRow[];
    rows: BotRow[];
    selectedIds: number[];
    colors: Map<number, string>;
    granularity: TimeGranularity;
};

const METRIC_LABEL: Record<TrendMetric, string> = {
    total: 'Всего',
    success: 'Успешные',
    failure: 'Ошибки',
    successRate: 'Success rate',
};

export function SelectedBotsTrend(props: Props) {
    const { timeRows, rows, selectedIds, colors, granularity } = props;
    const [metric, setMetric] = React.useState<TrendMetric>('total');

    const labelById = React.useMemo(() => {
        const map = new Map<number, string>();
        for (const row of rows) {
            map.set(row.botId, row.label);
        }
        return map;
    }, [rows]);

    const series = React.useMemo(() => {
        const isRate = metric === 'successRate';
        return selectedIds.map((id) => ({
            dataKey: trendDataKey(id, metric),
            label: labelById.get(id) ?? `#${id}`,
            color: colors.get(id) ?? '#1976d2',
            connectNulls: true,
            curve: 'monotoneX' as const,
            showMark: timeRows.length <= 40 && selectedIds.length <= 12,
            valueFormatter: (v: number | null) => {
                if (v == null) {
                    return '—';
                }
                return isRate ? `${v.toFixed(1)}%` : v.toLocaleString('ru-RU');
            },
        }));
    }, [selectedIds, metric, labelById, colors, timeRows.length]);

    if (timeRows.length === 0) {
        return null;
    }

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    flexWrap: 'wrap',
                    mb: 1,
                }}
            >
                <Box>
                    <Typography level="title-sm">Тренд выбранных ботов</Typography>
                    <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                        {selectedIds.length === 0
                            ? 'Выберите ботов в таблице'
                            : `${selectedIds.length} бот(ов) на графике`}
                    </Typography>
                </Box>
                <ToggleButtonGroup
                    size="sm"
                    value={metric}
                    onChange={(_, v) => v && setMetric(v as TrendMetric)}
                >
                    <Button value="total">{METRIC_LABEL.total}</Button>
                    <Button value="success">{METRIC_LABEL.success}</Button>
                    <Button value="failure">{METRIC_LABEL.failure}</Button>
                    <Button value="successRate">{METRIC_LABEL.successRate}</Button>
                </ToggleButtonGroup>
            </Box>
            {selectedIds.length === 0 ? (
                <Typography level="body-sm" sx={{ color: 'neutral.500', py: 2 }}>
                    Нет выбранных ботов
                </Typography>
            ) : (
                <LineChart
                    dataset={timeRows}
                    xAxis={[
                        {
                            dataKey: 'x',
                            scaleType: 'time',
                            valueFormatter: (v: Date) => formatBucketLabel(v, granularity),
                        },
                    ]}
                    yAxis={[
                        {
                            min: 0,
                            max: metric === 'successRate' ? 100 : undefined,
                            valueFormatter: (v: number) =>
                                metric === 'successRate'
                                    ? `${Math.round(v)}%`
                                    : v.toLocaleString('ru-RU'),
                        },
                    ]}
                    series={series}
                    height={360}
                    margin={{ top: 16, right: 16, bottom: 64, left: 56 }}
                    slotProps={{
                        legend: {
                            position: { vertical: 'bottom', horizontal: 'middle' },
                        },
                    }}
                />
            )}
        </Box>
    );
}
